<script setup>
import { computed, ref } from 'vue';
import { Head, Link, useForm, usePage } from '@inertiajs/vue3';
import { EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/outline';
import { useI18n } from '@/Composables/i18n.js';
import { useTitle as title } from '@/Composables/title.js';
import JetAuthenticationCard from '@/Components/AuthenticationCard.vue';
import JetButton from '@/Components/Button.vue';
import JetInput from '@/Components/Input.vue';
import JetInputHelp from '@/Components/InputHelp.vue';
import JetSelect from '@/Components/FormSelect.vue';
import JetLabel from '@/Components/Label.vue';
import JetValidationErrors from '@/Components/ValidationErrors.vue';
import { useTrackEvent } from '@/Composables/gtmTrackEvent.js';
import { digest } from '@/Composables/digest.js';

const page = usePage();
const locale = computed(() => page.props.locale);

defineProps({
    hasIntendedCheckoutRoute: {
        type: Boolean,
        default: false,
    },
});

const { getTranslationsByPrefix } = useI18n();

const form = useForm({
    email: '',
    password: '',
    terms: false,
    language: locale.value || 'de',
});

const showPassword = ref(false);

const submit = () => form.post(route('register'), {
    onFinish: () => form.reset('password'),
    onSuccess({ props: { auth } }) {
        if (auth.user) {
            digest(auth.user).then(digestHex => {
                /* eslint-disable camelcase */
                useTrackEvent('sign_up_started', {
                    user_id: auth.user.id,
                    user_data: {
                        sha256_email_address: digestHex,
                    },
                });
                /* eslint-enable camelcase */
            });
        }
    },
});
</script>

<template>
    <Head>
        <title>{{ title($t('auth.register.meta.title')) }}</title>
        <meta
            name="description"
            :content="$t('auth.register.meta.description')"
        >
    </Head>

    <JetAuthenticationCard :has-error="hasIntendedCheckoutRoute">
        <template #title>
            {{ $t('auth.register.cardTitle') }}
        </template>

        <template #error>
            {{ $t('auth.register.alert.hasIntendedCheckoutRoute') }}
        </template>

        <template #description>
            <p>
                {{ $t('auth.register.cardDescription') }}
            </p>
        </template>

        <JetValidationErrors class="my-4" />

        <form
            class="space-y-5"
            @submit.prevent="submit"
        >
            <div class="mt-4">
                <JetLabel
                    for="email"
                    :value="$t('auth.register.form.email.label')"
                />
                <JetInput
                    id="email"
                    v-model="form.email"
                    type="email"
                    class="mt-1 block w-full"
                    required
                    autocomplete="username"
                />
            </div>

            <div>
                <JetLabel
                    for="password"
                    :value="$t('auth.register.form.password.label')"
                />
                <div class="relative">
                    <JetInput
                        id="password"
                        v-model="form.password"
                        :type="showPassword ? 'text' : 'password'"
                        class="mt-1 block w-full pr-10"
                        required
                        autocomplete="new-password"
                    />
                    <div
                        class="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                        @click="showPassword = !showPassword"
                    >
                        <component
                            :is="showPassword ? EyeSlashIcon : EyeIcon"
                            class="h-5 w-5 text-gray-500"
                            aria-hidden="true"
                        />
                    </div>
                </div>
                <JetInputHelp :message="$t('auth.register.form.password.help')" />
            </div>

            <div>
                <JetLabel
                    for="language"
                    :value="$t('auth.register.form.language.label')"
                />
                <JetSelect
                    id="language"
                    v-model="form.language"
                    required
                    class="mt-1 block w-full"
                    :options="Object.entries(getTranslationsByPrefix('enums.language')).map(([value, text]) => ({
                        value,
                        text
                    }))"
                />
            </div>

            <div class="flex items-center justify-end pt-3">
                <Link
                    :href="route('login')"
                    class="font-semibold text-deep-teal-700 hover:text-deep-teal-900 hover:underline focus:underline focus:outline-none focus:text-deep-teal-900"
                >
                    {{ $t('auth.register.actions.login') }}
                </Link>

                <JetButton
                    class="ml-6"
                    :class="{ 'opacity-25': form.processing }"
                    :disabled="form.processing"
                >
                    {{ $t('auth.register.form.submit') }}
                </JetButton>
            </div>
        </form>
    </JetAuthenticationCard>
</template>
